import React, { useState, useEffect } from 'react';
import { Network, Save, AlertCircle, CheckCircle, Users, Lock } from 'lucide-react';
import { getCygnusSettings, upsertCygnusSettings } from '../../api';

const NovaConfig = () => {
  const [novaPublic, setNovaPublic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', message }

  useEffect(() => {
    getCygnusSettings()
      .then(res => {
        setNovaPublic(res.data?.novaPublic ?? false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await upsertCygnusSettings({ novaPublic });
      setStatus({ type: 'success', message: 'Configuration Nova sauvegardée.' });
    } catch {
      setStatus({ type: 'error', message: 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
        Chargement…
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
          <Network size={20} className="text-purple-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Nova — Visibilité</h2>
          <p className="text-sm text-gray-500">Contrôle de l'accès à l'orchestrateur IA Nova</p>
        </div>
      </div>

      {/* Toggle card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <p className="text-sm text-gray-600">
          Par défaut, le bouton Nova dans le header n'est visible que par les administrateurs globaux.
          Activez l'option ci-dessous pour le rendre accessible à tous les utilisateurs connectés.
        </p>

        <div
          className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
            novaPublic
              ? 'border-purple-400 bg-purple-50'
              : 'border-gray-200 bg-gray-50'
          }`}
          onClick={() => setNovaPublic(!novaPublic)}
        >
          <div className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${novaPublic ? 'bg-purple-500' : 'bg-gray-300'}`}>
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${novaPublic ? 'translate-x-7' : 'translate-x-1'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {novaPublic
                ? <Users size={15} className="text-purple-600" />
                : <Lock size={15} className="text-gray-500" />
              }
              <span className="font-medium text-sm text-gray-800">
                {novaPublic ? 'Visible par tous les utilisateurs' : 'Visible uniquement par les admins'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {novaPublic
                ? 'Tous les utilisateurs connectés voient le bouton Nova dans le header.'
                : 'Seuls les administrateurs globaux ont accès à Nova.'}
            </p>
          </div>
        </div>

        {status && (
          <div className={`flex items-center gap-2 text-sm p-3 rounded-lg ${
            status.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {status.type === 'success'
              ? <CheckCircle size={15} />
              : <AlertCircle size={15} />
            }
            {status.message}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
        >
          <Save size={15} />
          {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </button>
      </div>

      <p className="text-xs text-gray-400 italic">
        Note : Nova route les questions vers Cygnus (support app), Orion (RH) ou Lyra (protocoles) selon le contenu du message.
      </p>
    </div>
  );
};

export default NovaConfig;
